/**
 * The root module of the Vuex store provides root level access to deep actions
 * and choreographs more complex actions by composing them from other modules.
 * @file
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 */
import Vue from 'vue'
import Vuex from 'vuex'
import { getField, updateField } from 'vuex-map-fields'
import createLogger from 'vuex/dist/logger'
import { get as idbGet } from 'idb-keyval'
import lodashGet from 'lodash.get'
import lodashHas from 'lodash.has'

import constants from '@/store/modules/config/constants'
import properties from '@/store/modules/config/properties'
import version from '@/store/modules/config/version'

import calendarItems from '@/store/modules/calendarItems'
import auth from '@/store/modules/auth'
import docs from '@/store/modules/docs'
import echo from '@/store/modules/echo'
import events from '@/store/modules/events'
import files from '@/store/modules/files'
import jobs from '@/store/modules/jobs'
import memos from '@/store/modules/memos'
import mobile from '@/store/modules/mobile'
import notifications from '@/store/modules/notifications'
import routing from '@/store/modules/routing'
import ui from '@/store/modules/ui'
import upload from '@/store/modules/upload'
import users from '@/store/modules/users'
import premedia from '@/store/modules/premedia'
import premedia_search from '@/store/modules/premedia_search'
import attachments from '@/store/modules/attachments'
import attachment_queue_manager from '@/store/modules/attachment_queue_manager'
import onboarding from '@/store/modules/onboarding'
import snackbar from '@/store/modules/snackbar'
import persistence from '@/store/modules/persistence'
import global_action_prompt from '@/store/modules/global_action_prompt'
import work_in_progress from '@/store/modules/work_in_progress'



import { VUEX_APP_INIT, VUEX_APP_INITIALIZED } from '@/store/constants/app'
import { VUEX_AUTH_HYDRATE_FROM_TOKEN } from '@/store/constants/auth'

Vue.use(Vuex)

const debug = process.env.NODE_ENV !== 'production'

/**
 * The root module of our Vuex store
 * @module store
 */
export default new Vuex.Store({
  modules: {
		auth,
		calendarItems,
		constants,
		docs,
		echo,
		events,
		files,
		jobs,
		memos,
		mobile,
		notifications,
		properties,
		routing,
		ui,
		upload,
		users,
		version,
		premedia,
		premedia_search,
		attachments,
		attachment_queue_manager,
		onboarding,
		snackbar,
        persistence,
		global_action_prompt,
		work_in_progress
	},
	state: {
		initializing: false,
		initialized: false,
		lastSuccess: null,
		lastError: null
	},

	getters: {
		getField:getField,

		getValueByPath: (state) => (path) => {
			if (lodashHas(state, path)) {
				return lodashGet(state, path)
			}
			return null
		},

		getLastError: (state) => {
			return state.lastError
		}
	},

	actions: {
		/**
		 * When the app is launched, this action will handle any initialization of
		 * the store and any other things that need doing. It should only ever be
		 * called ONCE in the main.js file as a prerequisite to creating the App
		 * instance. If it fails, so too does the app.
		 *
		 * @function
		 * @param {object} context
		 */
		[VUEX_APP_INIT]: async ({ commit, dispatch, getters }) => {
			await idbGet(process.env.VUE_APP_SILA_2_USER_TOKEN).then(async (token) => {
				commit(VUEX_APP_INIT)

				if (token && token!== undefined && token !== null) {
					await dispatch(VUEX_AUTH_HYDRATE_FROM_TOKEN, token).then(() => {
						commit(VUEX_APP_INITIALIZED)
					})
				} else {
					commit(VUEX_APP_INITIALIZED)
				}
			})
		},


		/**
		 * Sets a globally accessible mobile property
		 * @function
		 * @param {mixed} context
		 * @param {mixed} payload
		 */
		global_setMobileState: ({ dispatch }, payload) => {
			dispatch('setMobileState', payload)
		},

		/**
		 * Sets a globally accessible desktop property
		 * @param {*} context
		 * @param {*} payload
		 */
		global_setBrowserState: ({ dispatch }, payload) => {
			dispatch('setBrowserState', payload)
		},

	},

	mutations: {
		updateField,

		[VUEX_APP_INIT]: (state) => {
			state.initializing = true
		},

		[VUEX_APP_INITIALIZED]: (state) => {
			state.initializing = false
			state.initialized = true
		}
	},

	strict: debug,
	plugins: process.env.NODE_ENV === 'development' ? [createLogger({ 'collapsed': true })] : []
})